import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
    Button,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDeletePaymentSchedule } from "../../services/agreementsService";
import { formatDecimalNumber } from "../../utils";
import { PaymentScheduleDialog } from "./PaymentSchedules/PaymentScheduleDialog";

export function PaymentSchedulesTab({ dataLocal, setDataLocal }) {
    const { t } = useTranslation();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(null);

    const deleteScheduleMutation = useDeletePaymentSchedule({
        onSuccess: () => {
            toast.success(t("paymentSchedules.deleteSuccess"));
        },
        onError: () => {
            toast.error(t("paymentSchedules.deleteError"));
        },
    });

    const handleEdit = (schedule) => {
        setSelectedSchedule(schedule);
        setDialogOpen(true);
    };

    const handleDelete = (scheduleId) => {
        if (window.confirm(t("paymentSchedules.confirmDelete"))) {
            deleteScheduleMutation.mutate({ scheduleId });
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedSchedule(null);
    };

    const formatInstallmentsBreakdown = (schedule) => {
        return schedule.installments
            .sort((a, b) => a.installment_order - b.installment_order)
            .map((inst) => `${formatDecimalNumber(inst.installment_percentage)}%`)
            .join(" - ");
    };

    return (
        <>
            <Grid container spacing={2} justifyContent="left" sx={{ mb: 2 }}>
                <Grid item>
                    <Button variant="contained" onClick={() => setDialogOpen(true)}>
                        {t("paymentSchedules.addSchedule")}
                    </Button>
                </Grid>
            </Grid>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: "primary.main" }}>
                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                                {t("paymentSchedules.table.scheduleName")}
                            </TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>
                                {t("paymentSchedules.table.installmentsCount")}
                            </TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>
                                {t("paymentSchedules.table.installmentsBreakdown")}
                            </TableCell>
                            <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                                {t("paymentSchedules.table.actions")}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dataLocal.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    {t("common.noData")}
                                </TableCell>
                            </TableRow>
                        ) : (
                            dataLocal.map((schedule) => (
                                <TableRow
                                    key={schedule.id}
                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                >
                                    <TableCell component="th" scope="row">
                                        {schedule.schedule_name}
                                    </TableCell>
                                    <TableCell align="center">
                                        {schedule.installments.length}
                                    </TableCell>
                                    <TableCell align="center">
                                        {formatInstallmentsBreakdown(schedule)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEdit(schedule)}
                                            sx={{ mr: 1 }}
                                        >
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDelete(schedule.id)}
                                        >
                                            <DeleteOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <PaymentScheduleDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                schedule={selectedSchedule}
            />
        </>
    );
}
